import { TIMEZONE, TODAY_FORMATTED } from '@bitstopco/bitstop-theme/constants';
import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';

import api from '@/store/api';
import { athena } from '@/store/api/instances';

import type { TransactionsStatsRequest, TransactionsStatsResponse } from '@/types';

export const DEFAULT_SALES_STATS_PAYLOAD: TransactionsStatsRequest = {
  timezone: TIMEZONE,
};

/**
 * Sales RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
export const SalesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSalesStats: build.query<TransactionsStatsResponse, TransactionsStatsRequest | void>({
      query: (payload = DEFAULT_SALES_STATS_PAYLOAD) => {
        // Create date range filters for Athena endpoint
        const dateRangeFilters: Array<{
          property: string;
          operator: string;
          value: string;
        }> = [];

        if (payload?.range_start && payload?.range_end) {
          dateRangeFilters.push(
            {
              property: 'created_at',
              operator: 'greater_than_or_equal',
              value: payload.range_start,
            },
            {
              property: 'created_at',
              operator: 'less_than_or_equal',
              value: payload.range_end,
            },
          );
        } else {
          // Default to today's date range
          dateRangeFilters.push(
            {
              property: 'created_at',
              operator: 'greater_than_or_equal',
              value: TODAY_FORMATTED,
            },
            {
              property: 'created_at',
              operator: 'less_than_or_equal',
              value: TODAY_FORMATTED,
            },
          );
        }

        const data = {
          filter: {
            operands: dateRangeFilters,
            operator: 'and',
          },
        };

        return athena({
          url: 'v2/transactions/stats',
          method: 'POST',
          data,
        });
      },
      transformResponse: (response: { data: TransactionsStatsResponse }) => {
        const sales = response.data;
        const _sales: any = {
          // Match exactly what the original Ergot endpoint provided
          total_count: sales.total_transactions,
          total_sales: sales.total_sales,
          total_fees: sales.total_fees,
          total_btc: sales.total_btc,
        };
        Object.keys(_sales).forEach((key) => {
          _sales[key] = ['total_transactions', 'total_count'].includes(key)
            ? _sales[key]
            : formatCurrency(_sales[key]);
        });

        return _sales;
      },
      providesTags: ['Sales'],
    }),
  }),
});

export const { useGetSalesStatsQuery } = SalesApi;

export const {
  endpoints: { getSalesStats },
} = SalesApi;
